import { useState, useEffect } from 'react';
import { useConfirm } from './ui';
import {
  CheckCircle,
  XCircle,
  Edit3,
  AlertTriangle,
  Filter,
  ChevronDown,
  ChevronUp,
  Brain,
  BookOpen,
  Layers,
  RefreshCw,
  CheckSquare,
  Square,
  Trash2,
  Save,
  X
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { authenticatedFetch } from '../lib/auth';
import type { Question, QuestionStats, QuestionStatus, QuestionDifficulty, BloomsLevel } from '../types';

interface QuestionReviewProps {
  studyPlanId: string;
  studyPlanTitle: string;
  onBack?: () => void;
}

// Bloom's taxonomy levels in Norwegian
const BLOOMS_LABELS: Record<BloomsLevel, string> = {
  remember: 'Huske',
  understand: 'Forstå',
  apply: 'Anvende',
  analyze: 'Analysere',
  evaluate: 'Vurdere',
  create: 'Skape'
};

const DIFFICULTY_LABELS: Record<QuestionDifficulty, string> = {
  easy: 'Lett',
  medium: 'Middels',
  hard: 'Vanskelig'
};

const STATUS_LABELS: Record<QuestionStatus, string> = {
  draft: 'Ubehandlet',
  approved: 'Godkjent',
  rejected: 'Avvist',
  needs_revision: 'Trenger revisjon',
  archived: 'Arkivert'
};

const STATUS_COLORS: Record<QuestionStatus, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  needs_revision: 'bg-kf-orange-100 text-kf-orange-800',
  archived: 'bg-gray-100 text-gray-600'
};

export default function QuestionReview({ studyPlanId, studyPlanTitle, onBack }: QuestionReviewProps) {
  const { confirm } = useConfirm();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [stats, setStats] = useState<QuestionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<QuestionStatus | 'all'>('draft');
  const [difficultyFilter, setDifficultyFilter] = useState<QuestionDifficulty | 'all'>('all');
  const [moduleFilter, setModuleFilter] = useState<string>('all');

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Expanded questions
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Edit mode
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Question>>({});

  // Available modules for filter
  const [modules, setModules] = useState<Array<{ id: string; title: string }>>([]);

  useEffect(() => {
    loadQuestions();
    loadStats();
    loadModules();
  }, [studyPlanId, statusFilter, difficultyFilter, moduleFilter]);

  async function loadQuestions() {
    setLoading(true);
    try {
      let url = `/api/questions/study-plan/${studyPlanId}?limit=200`;

      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (moduleFilter !== 'all') {
        url += `&moduleId=${moduleFilter}`;
      }

      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error('Kunne ikke hente spørsmål');

      const data = await response.json();

      // Filter by difficulty client-side if needed
      // Backend returns { questions: [...], total: N }
      let filtered = data.questions || data;
      if (difficultyFilter !== 'all') {
        filtered = data.filter((q: Question) => q.difficulty === difficultyFilter);
      }

      setQuestions(filtered);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const response = await authenticatedFetch(`/api/questions/study-plan/${studyPlanId}/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }

  async function loadModules() {
    try {
      const { data } = await supabase
        .from('study_plan_modules')
        .select('id, title')
        .eq('study_plan_id', studyPlanId)
        .order('display_order');

      if (data) {
        setModules(data);
      }
    } catch (err) {
      console.error('Failed to load modules:', err);
    }
  }

  async function updateQuestionStatus(questionId: string, status: QuestionStatus, reviewNotes?: string) {
    try {
      const response = await authenticatedFetch(`/api/questions/${questionId}/review`, {
        method: 'PATCH',
        body: JSON.stringify({ status, reviewNotes })
      });

      if (!response.ok) throw new Error('Kunne ikke oppdatere status');

      // Update local state
      setQuestions(prev =>
        prev.map(q => q.id === questionId ? { ...q, status, review_notes: reviewNotes || null } : q)
      );

      // Reload stats
      loadStats();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function bulkUpdateStatus(status: QuestionStatus) {
    if (selectedIds.size === 0) return;

    try {
      const response = await authenticatedFetch('/api/questions/bulk-review', {
        method: 'POST',
        body: JSON.stringify({
          questionIds: Array.from(selectedIds),
          status
        })
      });

      if (!response.ok) throw new Error('Kunne ikke oppdatere spørsmål');

      // Clear selection and reload
      setSelectedIds(new Set());
      loadQuestions();
      loadStats();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function saveQuestion(questionId: string) {
    try {
      const response = await authenticatedFetch(`/api/questions/${questionId}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });

      if (!response.ok) throw new Error('Kunne ikke lagre spørsmål');

      const updated = await response.json();

      setQuestions(prev =>
        prev.map(q => q.id === questionId ? { ...q, ...updated } : q)
      );

      setEditingId(null);
      setEditForm({});
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function deleteQuestion(questionId: string) {
    const confirmed = await confirm({
      title: 'Slett spørsmål',
      message: 'Er du sikker på at du vil slette dette spørsmålet?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(`/api/questions/${questionId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Kunne ikke slette spørsmål');

      setQuestions(prev => prev.filter(q => q.id !== questionId));
      loadStats();
    } catch (err: any) {
      setError(err.message);
    }
  }

  function toggleExpanded(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelected(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function selectAll() {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q.id)));
    }
  }

  function startEditing(question: Question) {
    setEditingId(question.id);
    setEditForm({
      question_text: question.question_text,
      difficulty: question.difficulty,
      explanation: question.explanation,
      answer_options: question.answer_options,
      points: question.points
    });
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        {onBack && (
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800 mb-4 flex items-center gap-1"
          >
            &larr; Tilbake
          </button>
        )}
        <h1 className="text-2xl font-bold text-gray-900">Spørsmålsgjennomgang</h1>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-sm text-gray-500">Studieplan:</span>
          <span className="text-lg font-medium text-kf-primary">{studyPlanTitle}</span>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.draft}</div>
            <div className="text-sm text-yellow-600">Ubehandlet</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.approved}</div>
            <div className="text-sm text-green-600">Godkjent</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
            <div className="text-sm text-red-600">Avvist</div>
          </div>
          <div className="bg-kf-orange-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-kf-orange-700">{stats.needs_revision}</div>
            <div className="text-sm text-kf-orange-600">Revisjon</div>
          </div>
          <div className="bg-kf-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-kf-blue-700">{stats.total}</div>
            <div className="text-sm text-kf-blue-600">Totalt</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as QuestionStatus | 'all')}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Alle statuser</option>
            <option value="draft">Ubehandlet</option>
            <option value="approved">Godkjent</option>
            <option value="rejected">Avvist</option>
            <option value="needs_revision">Trenger revisjon</option>
          </select>

          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value as QuestionDifficulty | 'all')}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Alle vanskelighetsgrader</option>
            <option value="easy">Lett</option>
            <option value="medium">Middels</option>
            <option value="hard">Vanskelig</option>
          </select>

          {modules.length > 0 && (
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">Alle moduler</option>
              {modules.map(m => (
                <option key={m.id} value={m.id}>{m.title}</option>
              ))}
            </select>
          )}

          <button
            onClick={() => loadQuestions()}
            className="ml-auto flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
          >
            <RefreshCw size={16} />
            Oppdater
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="bg-kf-blue-50 rounded-lg p-4 mb-4 flex items-center justify-between">
          <span className="text-kf-blue-700 font-medium">
            {selectedIds.size} spørsmål valgt
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => bulkUpdateStatus('approved')}
              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
            >
              <CheckCircle size={16} />
              Godkjenn alle
            </button>
            <button
              onClick={() => bulkUpdateStatus('rejected')}
              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"
            >
              <XCircle size={16} />
              Avvis alle
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-300"
            >
              Avbryt
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4 flex items-center gap-2">
          <AlertTriangle size={18} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <RefreshCw size={32} className="animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Laster spørsmål...</p>
        </div>
      )}

      {/* Questions List */}
      {!loading && questions.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <BookOpen size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Ingen spørsmål funnet med valgte filter</p>
        </div>
      )}

      {!loading && questions.length > 0 && (
        <div className="space-y-4">
          {/* Select All */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button onClick={selectAll} className="flex items-center gap-2 hover:text-gray-800">
              {selectedIds.size === questions.length ? (
                <CheckSquare size={18} className="text-kf-blue-600" />
              ) : (
                <Square size={18} />
              )}
              Velg alle ({questions.length})
            </button>
          </div>

          {/* Question Cards */}
          {questions.map(question => (
            <QuestionCard
              key={question.id}
              question={question}
              isExpanded={expandedIds.has(question.id)}
              isSelected={selectedIds.has(question.id)}
              isEditing={editingId === question.id}
              editForm={editForm}
              onToggleExpand={() => toggleExpanded(question.id)}
              onToggleSelect={() => toggleSelected(question.id)}
              onApprove={() => updateQuestionStatus(question.id, 'approved')}
              onReject={() => updateQuestionStatus(question.id, 'rejected')}
              onNeedsRevision={(notes) => updateQuestionStatus(question.id, 'needs_revision', notes)}
              onEdit={() => startEditing(question)}
              onSave={() => saveQuestion(question.id)}
              onCancelEdit={() => { setEditingId(null); setEditForm({}); }}
              onDelete={() => deleteQuestion(question.id)}
              onEditFormChange={setEditForm}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Question Card Component
interface QuestionCardProps {
  question: Question;
  isExpanded: boolean;
  isSelected: boolean;
  isEditing: boolean;
  editForm: Partial<Question>;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  onNeedsRevision: (notes: string) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  onEditFormChange: (form: Partial<Question>) => void;
}

function QuestionCard({
  question,
  isExpanded,
  isSelected,
  isEditing,
  editForm,
  onToggleExpand,
  onToggleSelect,
  onApprove,
  onReject,
  onNeedsRevision,
  onEdit,
  onSave,
  onCancelEdit,
  onDelete,
  onEditFormChange
}: QuestionCardProps) {
  const [revisionNotes, setRevisionNotes] = useState('');
  const [showRevisionInput, setShowRevisionInput] = useState(false);

  return (
    <div className={`bg-white rounded-lg shadow border-l-4 ${
      question.status === 'approved' ? 'border-green-500' :
      question.status === 'rejected' ? 'border-red-500' :
      question.status === 'needs_revision' ? 'border-kf-orange-500' :
      'border-yellow-500'
    }`}>
      {/* Header */}
      <div className="p-4 flex items-start gap-4">
        <button onClick={onToggleSelect} className="mt-1">
          {isSelected ? (
            <CheckSquare size={20} className="text-kf-blue-600" />
          ) : (
            <Square size={20} className="text-gray-400" />
          )}
        </button>

        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {isEditing ? (
                <textarea
                  value={editForm.question_text || ''}
                  onChange={(e) => onEditFormChange({ ...editForm, question_text: e.target.value })}
                  className="w-full border rounded-lg p-2 text-gray-900"
                  rows={3}
                />
              ) : (
                <p className="text-gray-900 font-medium">{question.question_text}</p>
              )}

              {/* Metadata badges */}
              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[question.status]}`}>
                  {STATUS_LABELS[question.status]}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  question.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                  question.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                  'bg-kf-blue-100 text-kf-blue-700'
                }`}>
                  {DIFFICULTY_LABELS[question.difficulty]}
                </span>
                {question.blooms_level && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                    {BLOOMS_LABELS[question.blooms_level]}
                  </span>
                )}
                {question.ai_generated && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700 flex items-center gap-1">
                    <Brain size={12} />
                    KI-generert
                    {question.ai_confidence_score && (
                      <span>({Math.round(question.ai_confidence_score * 100)}%)</span>
                    )}
                  </span>
                )}
                {question.module_title && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 flex items-center gap-1">
                    <Layers size={12} />
                    {question.module_title}
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={onToggleExpand}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t pt-4">
          {/* Answer Options */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Svaralternativer:</h4>
            {isEditing ? (
              <div className="space-y-2">
                {(editForm.answer_options || question.answer_options).map((opt, idx) => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={opt.is_correct}
                      onChange={(e) => {
                        const newOptions = [...(editForm.answer_options || question.answer_options)];
                        newOptions[idx] = { ...newOptions[idx], is_correct: e.target.checked };
                        onEditFormChange({ ...editForm, answer_options: newOptions });
                      }}
                      className="rounded"
                    />
                    <input
                      type="text"
                      value={opt.text}
                      onChange={(e) => {
                        const newOptions = [...(editForm.answer_options || question.answer_options)];
                        newOptions[idx] = { ...newOptions[idx], text: e.target.value };
                        onEditFormChange({ ...editForm, answer_options: newOptions });
                      }}
                      className="flex-1 border rounded px-2 py-1 text-sm"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <ul className="space-y-1">
                {question.answer_options.map(opt => (
                  <li
                    key={opt.id}
                    className={`flex items-center gap-2 text-sm ${
                      opt.is_correct ? 'text-green-700 font-medium' : 'text-gray-600'
                    }`}
                  >
                    {opt.is_correct ? (
                      <CheckCircle size={16} className="text-green-500" />
                    ) : (
                      <span className="w-4 h-4 rounded-full border border-gray-300" />
                    )}
                    {opt.text}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Explanation */}
          {(question.explanation || isEditing) && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Forklaring:</h4>
              {isEditing ? (
                <textarea
                  value={editForm.explanation || ''}
                  onChange={(e) => onEditFormChange({ ...editForm, explanation: e.target.value })}
                  className="w-full border rounded-lg p-2 text-sm"
                  rows={2}
                />
              ) : (
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{question.explanation}</p>
              )}
            </div>
          )}

          {/* Source Reference */}
          {question.source_reference && (
            <div className="mb-4 text-sm text-gray-500 flex items-center gap-2">
              <BookOpen size={14} />
              Kilde: {question.source_reference}
            </div>
          )}

          {/* Review Notes */}
          {question.review_notes && (
            <div className="mb-4 bg-kf-orange-50 p-3 rounded">
              <p className="text-sm text-kf-orange-700">
                <strong>Merknader:</strong> {question.review_notes}
              </p>
            </div>
          )}

          {/* Revision Notes Input */}
          {showRevisionInput && (
            <div className="mb-4">
              <textarea
                value={revisionNotes}
                onChange={(e) => setRevisionNotes(e.target.value)}
                placeholder="Beskriv hva som må endres..."
                className="w-full border rounded-lg p-2 text-sm mb-2"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onNeedsRevision(revisionNotes);
                    setShowRevisionInput(false);
                    setRevisionNotes('');
                  }}
                  className="bg-kf-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-kf-orange-700"
                >
                  Send til revisjon
                </button>
                <button
                  onClick={() => setShowRevisionInput(false)}
                  className="text-gray-600 px-3 py-1 rounded text-sm hover:bg-gray-100"
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {isEditing ? (
              <>
                <button
                  onClick={onSave}
                  className="bg-kf-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-kf-blue-700 flex items-center gap-2"
                >
                  <Save size={16} />
                  Lagre
                </button>
                <button
                  onClick={onCancelEdit}
                  className="text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-100"
                >
                  Avbryt
                </button>
              </>
            ) : (
              <>
                {question.status !== 'approved' && (
                  <button
                    onClick={onApprove}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle size={16} />
                    Godkjenn
                  </button>
                )}
                {question.status !== 'rejected' && (
                  <button
                    onClick={onReject}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"
                  >
                    <XCircle size={16} />
                    Avvis
                  </button>
                )}
                {question.status !== 'needs_revision' && (
                  <button
                    onClick={() => setShowRevisionInput(true)}
                    className="bg-kf-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-kf-orange-700 flex items-center gap-2"
                  >
                    <AlertTriangle size={16} />
                    Revisjon
                  </button>
                )}
                <button
                  onClick={onEdit}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-300 flex items-center gap-2"
                >
                  <Edit3 size={16} />
                  Rediger
                </button>
                <button
                  onClick={onDelete}
                  className="text-red-600 px-4 py-2 rounded-lg text-sm hover:bg-red-50 flex items-center gap-2 ml-auto"
                >
                  <Trash2 size={16} />
                  Slett
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
